import { useCallback, useEffect, useState } from "react";
import { useViewer } from "./ViewerProvider";
import type { BandState, EngagementState, RingState, SceneState, ViewerEventName, ViewerEventPayloads } from "../shared/api-contract";

/**
 * Holds the live RingState (v37: the wedding band plus its optional engagement
 * block). Same shape as `useBandState` / `useEngagementState` so consumers can
 * be swapped over mechanically; those two remain until ui/band and
 * ui/engagement are migrated.
 */
export function useRingState() {
  const { client, catalogs } = useViewer();
  const [state, setState] = useState<RingState | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await client.ringBuilder.getRingState();
      if (s) setState(s);
    } catch (err) {
      // A pre-v37 viewer REJECTS unknown methods (it does not hang). Leave the
      // state null so the builder shows its fallback instead of crashing.
      console.warn("[ring] getRingState unavailable:", err);
    }
  }, [client]);

  useEffect(() => { void refresh(); }, [refresh]);

  const apply = useCallback(async (p: Promise<RingState | null>) => {
    const s = await p;
    if (s) setState(s);
  }, []);

  useEffect(() => client.on("ringStateChanged", ({ state: s }) => { if (s) setState(s); }), [client]);

  return { state, setState, apply, refresh, client, catalogs };
}

/**
 * Holds the live BandState. `apply()` runs a band setter (which returns the
 * fresh state) and stores the result; `refresh()` re-pulls from the viewer.
 * Sliders call setters fire-and-forget for instant updates, then `refresh()`
 * on step change keeps the snapshot current.
 */
export function useBandState() {
  const { client, catalogs } = useViewer();
  const [state, setState] = useState<BandState | null>(null);

  const refresh = useCallback(async () => {
    const s = await client.band.getBandState();
    if (s) setState(s);
  }, [client]);

  useEffect(() => { void refresh(); }, [refresh]);

  const apply = useCallback(async (p: Promise<BandState | null>) => {
    const s = await p;
    if (s) setState(s);
  }, []);

  // Keep the live snapshot (and the spec panel) current after every rebuild,
  // including debounced/fire-and-forget slider edits on the active ring.
  useEffect(() => client.on("bandStateChanged", ({ state: s }) => { if (s) setState(s); }), [client]);

  return { state, setState, apply, refresh, client, catalogs };
}

/**
 * Holds the live EngagementState. Mirrors `useBandState`: `apply()` runs an
 * engagement setter (which returns the fresh state) and stores the result;
 * `refresh()` re-pulls from the viewer; `engagementStateChanged` keeps the
 * snapshot current after any rebuild (draft end, design load, etc).
 */
export function useEngagementState() {
  const { client, catalogs } = useViewer();
  const [state, setState] = useState<EngagementState | null>(null);

  const refresh = useCallback(async () => {
    const s = await client.engagement.getEngagementState();
    if (s) setState(s);
  }, [client]);

  useEffect(() => { void refresh(); }, [refresh]);

  const apply = useCallback(async (p: Promise<EngagementState | null>) => {
    const s = await p;
    if (s) setState(s);
  }, []);

  useEffect(() => client.on("engagementStateChanged", ({ state: s }) => { if (s) setState(s); }), [client]);

  return { state, setState, apply, refresh, client, catalogs };
}

/** Reads the initial SceneState once for toolbar control defaults. */
export function useSceneState() {
  const { client } = useViewer();
  const [scene, setScene] = useState<SceneState | null>(null);
  useEffect(() => { client.ring.getSceneState().then(setScene); }, [client]);
  return scene;
}

/** Subscribe to a viewer event for the component's lifetime. */
export function useViewerEvent<K extends ViewerEventName>(
  name: K,
  handler: (payload: ViewerEventPayloads[K]) => void,
) {
  const { client } = useViewer();
  useEffect(() => client.on(name, handler), [client, name, handler]);
}
